import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { DollarSign, Package, Users, ShoppingBag, Trash2, Plus, MapPin, Calendar, Image, Upload, Loader2 } from "lucide-react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const resolveImg = (u) => (u && u.startsWith("/api") ? `${API.replace(/\/api$/, "")}${u}` : u);

const Stat = ({ icon: Icon, label, value }) => (
  <div className="border-b border-r border-border p-6" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
    <Icon className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
    <p className="mt-4 font-display text-3xl font-extrabold tracking-tighter">{value}</p>
    <p className="mt-1 text-xs tracking-[0.2em] text-muted-foreground">{label.toUpperCase()}</p>
  </div>
);

const Field = ({ ...p }) => <input {...p} className="w-full border border-border bg-black px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-white" />;

export default function Admin() {
  const { user, loading } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState("overview");

  const stats = useQuery({ queryKey: ["admin-stats"], queryFn: async () => (await api.get("/admin/stats")).data, enabled: !!user });
  const orders = useQuery({ queryKey: ["admin-orders"], queryFn: async () => (await api.get("/admin/orders")).data, enabled: !!user });
  const users = useQuery({ queryKey: ["admin-users"], queryFn: async () => (await api.get("/admin/users")).data, enabled: !!user });
  const products = useQuery({ queryKey: ["admin-products"], queryFn: async () => (await api.get("/products")).data, enabled: !!user });
  const stores = useQuery({ queryKey: ["stores"], queryFn: async () => (await api.get("/stores")).data, enabled: !!user });
  const events = useQuery({ queryKey: ["events"], queryFn: async () => (await api.get("/events")).data, enabled: !!user });

  if (loading) return <div className="px-8 py-20 text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const s = stats.data || {};

  const updateOrder = async (id, patch) => {
    await api.patch(`/admin/orders/${id}`, patch);
    toast.success("Order updated");
    qc.invalidateQueries({ queryKey: ["admin-orders"] });
  };
  const buyLabel = async (id) => {
    toast.loading("Buying shipping label…", { id: `label-${id}` });
    try {
      const { data } = await api.post(`/admin/orders/${id}/label`);
      toast.success(`Label ready · ${data.carrier || ""} ${data.tracking_number || ""}`.trim(), { id: `label-${id}` });
      qc.invalidateQueries({ queryKey: ["admin-orders"] });
      if (data.label_url) window.open(data.label_url, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not buy label", { id: `label-${id}` });
    }
  };
  const del = async (url, keys) => { await api.delete(url); toast.success("Deleted"); keys.forEach((k) => qc.invalidateQueries({ queryKey: [k] })); };

  return (
    <div className="mx-auto max-w-[1600px] px-5 py-10 md:px-8" data-testid="admin-page">
      <Seo title="Admin — The Hoodie Project®" />
      <div className="mb-8 flex items-center justify-between border-b border-border pb-6">
        <div>
          <p className="text-xs tracking-[0.3em] text-muted-foreground">CONTROL ROOM</p>
          <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tighter md:text-5xl">ADMIN</h1>
        </div>
        <p className="text-xs text-muted-foreground">{user.email}</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-8 flex h-auto w-full flex-wrap justify-start gap-2 bg-transparent p-0">
          {["overview", "orders", "products", "users", "stores", "events", "appearance"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`tab-${t}`}
              className="rounded-none border border-border px-4 py-2 text-xs tracking-widest data-[state=active]:bg-white data-[state=active]:text-black">
              {t.toUpperCase()}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview">
          <div className="grid grid-cols-2 border-l border-t border-border lg:grid-cols-4">
            <Stat icon={DollarSign} label="Revenue" value={`$${(s.revenue ?? 0).toFixed(2)}`} />
            <Stat icon={ShoppingBag} label="Paid Orders" value={s.paid_orders ?? 0} />
            <Stat icon={Package} label="Products" value={s.products ?? 0} />
            <Stat icon={Users} label="Users" value={s.users ?? 0} />
            <Stat icon={ShoppingBag} label="Total Orders" value={s.total_orders ?? 0} />
            <Stat icon={Users} label="Sellers" value={s.sellers ?? 0} />
            <Stat icon={MapPin} label="Stores" value={s.stores ?? 0} />
            <Stat icon={Calendar} label="Events" value={s.events ?? 0} />
          </div>
        </TabsContent>

        {/* ORDERS */}
        <TabsContent value="orders">
          <OrdersTable orders={orders.data || []} onUpdate={updateOrder} onLabel={buyLabel} />
        </TabsContent>

        {/* PRODUCTS */}
        <TabsContent value="products">
          <ProductForm onDone={() => qc.invalidateQueries({ queryKey: ["admin-products"] })} />
          <div className="mt-6 border border-border">
            {(products.data || []).map((p) => (
              <div key={p.id} className="flex items-center justify-between border-b border-border p-4 last:border-b-0" data-testid={`admin-product-${p.id}`}>
                <div className="flex items-center gap-4">
                  <img src={p.images?.[0]} alt="" className="h-12 w-12 object-cover" />
                  <div>
                    <p className="text-sm">{p.name} <span className="text-muted-foreground">· ${p.price}</span></p>
                    <p className="text-xs text-muted-foreground">{p.origin} · {p.seller_name}</p>
                  </div>
                </div>
                <button onClick={() => del(`/products/${p.id}`, ["admin-products"])} data-testid={`delete-product-${p.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* USERS */}
        <TabsContent value="users">
          <div className="overflow-x-auto border border-border">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-xs tracking-widest text-muted-foreground">
                <th className="p-4">NAME</th><th className="p-4">EMAIL</th><th className="p-4">ROLE</th></tr></thead>
              <tbody>
                {(users.data || []).map((u) => (
                  <tr key={u.id} className="border-b border-border last:border-b-0" data-testid={`admin-user-${u.id}`}>
                    <td className="p-4">{u.name}</td><td className="p-4 text-muted-foreground">{u.email}</td>
                    <td className="p-4"><span className="border border-border px-2 py-1 text-xs tracking-widest">{u.role.toUpperCase()}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* STORES */}
        <TabsContent value="stores">
          <StoreForm onDone={() => qc.invalidateQueries({ queryKey: ["stores"] })} />
          <div className="mt-6 border border-border">
            {(stores.data || []).map((st) => (
              <div key={st.id} className="flex items-center justify-between border-b border-border p-4 last:border-b-0" data-testid={`admin-store-${st.id}`}>
                <p className="text-sm">{st.name} <span className="text-muted-foreground">· {st.city}, {st.state}</span></p>
                <button onClick={() => del(`/stores/${st.id}`, ["stores"])} data-testid={`delete-store-${st.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* EVENTS */}
        <TabsContent value="events">
          <EventForm onDone={() => qc.invalidateQueries({ queryKey: ["events"] })} />
          <div className="mt-6 border border-border">
            {(events.data || []).map((e) => (
              <div key={e.id} className="flex items-center justify-between border-b border-border p-4 last:border-b-0" data-testid={`admin-event-${e.id}`}>
                <p className="text-sm">{e.title} <span className="text-muted-foreground">· {e.date}</span></p>
                <button onClick={() => del(`/events/${e.id}`, ["events"])} data-testid={`delete-event-${e.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* APPEARANCE */}
        <TabsContent value="appearance">
          <AppearancePanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function OrdersTable({ orders, onUpdate, onLabel }) {
  if (orders.length === 0) return <p className="text-muted-foreground" data-testid="orders-empty">No orders yet.</p>;
  return (
    <div className="space-y-4">
      {orders.map((o) => (
        <div key={o.id} className="border border-border p-4" data-testid={`admin-order-${o.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm">{o.email || o.customer_email || "guest"} · <span className="text-muted-foreground">{(o.items || []).map((i) => `${i.name} ×${i.quantity}`).join(", ")}</span></p>
              <p className="text-xs text-muted-foreground">${(o.amount_total ?? o.amount ?? 0).toFixed(2)} · {new Date(o.created_at).toLocaleString()}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`border px-2 py-1 text-xs tracking-widest ${o.payment_status === "paid" ? "border-white text-white" : "border-border text-muted-foreground"}`}>{(o.payment_status || "pending").toUpperCase()}</span>
              <select value={o.fulfillment || "unfulfilled"} onChange={(e) => onUpdate(o.id, { fulfillment: e.target.value })} data-testid={`order-fulfillment-${o.id}`}
                className="border border-border bg-black px-2 py-1 text-xs">
                <option value="unfulfilled">UNFULFILLED</option>
                <option value="processing">PROCESSING</option>
                <option value="shipped">SHIPPED</option>
                <option value="delivered">DELIVERED</option>
              </select>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input defaultValue={o.tracking || ""} placeholder="Tracking #" data-testid={`order-tracking-${o.id}`}
              onBlur={(e) => e.target.value !== (o.tracking || "") && onUpdate(o.id, { tracking: e.target.value })}
              className="w-full max-w-xs border border-border bg-black px-3 py-2 text-xs outline-none focus:ring-1 focus:ring-white" />
            {o.label_url ? (
              <a href={o.label_url} target="_blank" rel="noreferrer" data-testid={`download-label-${o.id}`}
                className="border border-white px-3 py-2 text-xs font-bold tracking-widest hover:bg-white hover:text-black">DOWNLOAD LABEL</a>
            ) : o.payment_status === "paid" ? (
              <button onClick={() => onLabel(o.id)} data-testid={`buy-label-${o.id}`}
                className="bg-white px-3 py-2 text-xs font-bold tracking-widest text-black hover:bg-neutral-300">BUY SHIPPING LABEL</button>
            ) : null}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            {o.ship_free ? "Free shipping" : o.ship_service ? `${o.ship_service} · $${(o.shipping_amount ?? 0).toFixed(2)}` : "Shipping: n/a"}
            {o.carrier ? ` · ${o.carrier}` : ""}
          </p>
        </div>
      ))}
    </div>
  );
}

function ProductForm({ onDone }) {
  const [f, setF] = useState({ name: "", price: "", color: "Black", images: "", description: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/products", {
        name: f.name, price: parseFloat(f.price), description: f.description,
        colors: [f.color], sizes: ["M", "L", "XL", "XXL"],
        images: f.images.split(",").map((s) => s.trim()).filter(Boolean),
        origin: "official",
      });
      toast.success("Product added");
      setF({ name: "", price: "", color: "Black", images: "", description: "" });
      onDone();
    } catch { toast.error("Could not add product"); }
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-3 border border-border p-4 md:grid-cols-2" data-testid="product-form">
      <Field placeholder="Product name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="pf-name" />
      <Field placeholder="Price (e.g. 65)" type="number" step="0.01" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} required data-testid="pf-price" />
      <Field placeholder="Color" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} data-testid="pf-color" />
      <Field placeholder="Image URLs (comma separated)" value={f.images} onChange={(e) => setF({ ...f, images: e.target.value })} data-testid="pf-images" />
      <Field placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="pf-desc" />
      <button className="flex items-center justify-center gap-2 bg-white py-2 text-xs font-bold tracking-widest text-black hover:bg-neutral-300" data-testid="pf-submit"><Plus className="h-4 w-4" /> ADD PRODUCT</button>
    </form>
  );
}

function StoreForm({ onDone }) {
  const [f, setF] = useState({ name: "", address: "", city: "", state: "", hours: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/stores", f); toast.success("Store added"); setF({ name: "", address: "", city: "", state: "", hours: "" }); onDone(); }
    catch { toast.error("Could not add store"); }
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-3 border border-border p-4 md:grid-cols-3" data-testid="store-form">
      <Field placeholder="Store name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="sf-name" />
      <Field placeholder="Address" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} required data-testid="sf-address" />
      <Field placeholder="City" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} required data-testid="sf-city" />
      <Field placeholder="State" value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} data-testid="sf-state" />
      <Field placeholder="Hours" value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value })} data-testid="sf-hours" />
      <button className="flex items-center justify-center gap-2 bg-white py-2 text-xs font-bold tracking-widest text-black hover:bg-neutral-300" data-testid="sf-submit"><Plus className="h-4 w-4" /> ADD STORE</button>
    </form>
  );
}

function EventForm({ onDone }) {
  const [f, setF] = useState({ title: "", date: "", location: "", image: "", partner: "", description: "" });
  const submit = async (e) => {
    e.preventDefault();
    const slug = f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    try { await api.post("/events", { ...f, slug }); toast.success("Event added"); setF({ title: "", date: "", location: "", image: "", partner: "", description: "" }); onDone(); }
    catch { toast.error("Could not add event"); }
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-3 border border-border p-4 md:grid-cols-2" data-testid="event-form">
      <Field placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required data-testid="ef-title" />
      <Field placeholder="Date (2026-07-12)" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="ef-date" />
      <Field placeholder="Location" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="ef-location" />
      <Field placeholder="Partner (optional)" value={f.partner} onChange={(e) => setF({ ...f, partner: e.target.value })} data-testid="ef-partner" />
      <Field placeholder="Image URL" value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} data-testid="ef-image" />
      <Field placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="ef-desc" />
      <button className="flex items-center justify-center gap-2 bg-white py-2 text-xs font-bold tracking-widest text-black hover:bg-neutral-300 md:col-span-2" data-testid="ef-submit"><Plus className="h-4 w-4" /> ADD EVENT</button>
    </form>
  );
}

function ImageUploader({ label, field, value, onSaved }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(resolveImg(value));
  const inputRef = React.useRef();

  React.useEffect(() => { setPreview(resolveImg(value)); }, [value]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put("/admin/settings", { [field]: data.url });
      toast.success(`${label} updated`);
      onSaved();
    } catch (err) {
      toast.error("Upload failed — try a smaller image (max 10MB)");
    } finally { setUploading(false); }
  };

  return (
    <div className="border border-border p-5" data-testid={`uploader-${field}`}>
      <p className="mb-4 text-xs tracking-[0.2em] text-muted-foreground">{label.toUpperCase()}</p>
      <div className="aspect-[16/10] w-full overflow-hidden border border-border bg-muted">
        {preview ? <img src={preview} alt={label} className="h-full w-full object-cover" /> : null}
      </div>
      <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid={`file-input-${field}`} />
      <button onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={`upload-btn-${field}`}
        className="mt-4 flex w-full items-center justify-center gap-2 border border-border py-3 text-xs font-bold tracking-[0.2em] transition-colors hover:bg-white hover:text-black disabled:opacity-50">
        {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> UPLOADING…</> : <><Upload className="h-4 w-4" /> UPLOAD NEW IMAGE</>}
      </button>
      <p className="mt-3 text-[11px] text-muted-foreground">JPG, PNG or WEBP · up to 10MB · landscape works best.</p>
    </div>
  );
}

function AppearancePanel() {
  const qc = useQueryClient();
  const { data: settings = {} } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["settings"] });
  return (
    <div data-testid="appearance-panel">
      <div className="mb-6 flex items-center gap-2 text-sm text-muted-foreground">
        <Image className="h-4 w-4" /> Upload images straight from your computer. Changes go live instantly.
      </div>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <ImageUploader label="Home Hero Image" field="hero_image" value={settings.hero_image} onSaved={refresh} />
        <ImageUploader label="Community Banner" field="community_image" value={settings.community_image} onSaved={refresh} />
      </div>
    </div>
  );
}

